import { FilterIcon2 } from "../../../../assets/icons";
import Button from "../../../../common/components/xepp/button/Button";
import { useConfigLayout } from "../../../../hooks/useConfigLayout";
import styles from "./dashboard.module.scss";
import { DateRangeFilter } from "../../../../common/components/xepp/date-range-filter/DateRangeFilter";
import { useEffect, useMemo, useState } from "react";
import { useLazyGetAggregatedTransactionsQuery, useLazyGetUtilityDashboardSummaryQuery } from "../../../../service/transaction-service";
import { getErrorMessage } from "../../../../utils/error-handler";
import { endOfDay, nextDayStart, startOfDay } from "../../../../utils/date-params";
import { useLazyGetUserInfoQuery } from "../../../../service/user-service";
import { ClipLoader } from "react-spinners";
import { SummaryCard } from "./components/summary-card/SummaryCard";
import { AggregationType, getTransactionPurposeTypeString, TransactionPurposeType, UtilityDashboardSummary } from "../../../../model/transaction";
import { Money } from "../../../../model/common/money";
import CommonSelect, { CommonSelectItem } from "../../../../common/components/select/CommonSelect";
import UtilAdminDashboardChart, {
	UtilAdminDashboardChartGroup,
	UtilAdminDashboardChartProps,
} from "./components/util-admin-dashboard-chart/util-admin-dashboard-chart";
import { formatPeriodLabel, getPeriodKey, getPeriodsBetween } from "./dashboard-helper";
import { DistrictRevenue } from "./components/district-revenue/DistrictRevenue";
import InfoTooltip from "../../../../common/components/info-tooltip/InfoTooltip";
import FeatureLockedState from "../../../../common/components/feature-locked-state/FeatureLockedState";
import { FeatureFlagName } from "../../../../tenant/features/featureFlags";
import { useFeatureFlags } from "../../../../tenant/features/useFeatureFlag";
import { ADMIN_FEATURE_INFO } from "../../adminFeatureCatalogue";
import { hideLoader, showLoader } from "../../../../stores/slices/loader-slice";
import { useDispatch } from "react-redux";
import { useTenant } from "../../../../tenant/branding/useTenant";

export const UtilAdminDashboard = () => {
	useConfigLayout({ showBreadCrumb: false, showCountrySelector: false, utility: null, showExportButton: false });

	const now = new Date();
	const startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
	const dispatch = useDispatch();
	const { name } = useTenant();

	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [dateFrom, setDateFrom] = useState<Date | null>(startDate);
	const [dateTo, setDateTo] = useState<Date | null>(now);
	const [dashboardData, setDashboardData] = useState<UtilityDashboardSummary | null>(null);

	const [isDistrictFilterOpen, setIsDistrictFilterOpen] = useState(false);
	const { isEnabled: isFeatureEnabled, isLoading: areFlagsLoading } = useFeatureFlags();
	const districtAnalyticsEnabled = isFeatureEnabled(FeatureFlagName.ADMIN_DISTRICT_REVENUE_ANALYTICS);
	const districtAnalyticsInfo = ADMIN_FEATURE_INFO[FeatureFlagName.ADMIN_DISTRICT_REVENUE_ANALYTICS];
	const [districtDateFrom, setDistrictDateFrom] = useState<Date | null>(startDate);
	const [districtDateTo, setDistrictDateTo] = useState<Date | null>(now);
	const [isDistrictFetching, setIsDistrictFetching] = useState(false);

	const [isDateFilterOpen, setIsDateFilterOpen] = useState(false);
	const [fromDate, setFromDate] = useState<Date | null>(startDate);
	const [toDate, setToDate] = useState<Date | null>(now);
	const [aggregatedBy, setAggregatedBy] = useState<AggregationType | undefined>(AggregationType.DAY);
	const [dashboardChartProps, setDashboardChartProps] = useState<UtilAdminDashboardChartProps | null>();

	const [getUtilityDashboardSummary, { isLoading, isFetching }] = useLazyGetUtilityDashboardSummaryQuery();
	const [getAggregatedTransactions, { data: aggregatedTransactions }] = useLazyGetAggregatedTransactionsQuery();
	const [getUserInfo, { data: userInfo }] = useLazyGetUserInfoQuery();
	const transactionPurposeTypes = [TransactionPurposeType.Billing, TransactionPurposeType.Vending];

	const popupFilterValues = useMemo(
		() => transactionPurposeTypes.map(x => new CommonSelectItem(x.valueOf(), getTransactionPurposeTypeString(x))),
		[transactionPurposeTypes]
	);
	const [transactionPurposeType, setTransactionPurposeType] = useState<CommonSelectItem | undefined>(popupFilterValues[0]);

	const handleApplyDistrictFilter = (from: Date | null, to: Date | null) => {
		setDistrictDateFrom(from);
		setDistrictDateTo(to);
	};

	const formatDistrictDateRange = () => {
		if (!districtDateFrom || !districtDateTo) return "Select Period";
		const options: Intl.DateTimeFormatOptions = { month: "short", day: "2-digit", year: "numeric" };
		return `${districtDateFrom.toLocaleDateString("en-US", options)} - ${districtDateTo.toLocaleDateString("en-US", options)}`;
	};

	const handleApplyDateFilter = (from: Date | null, to: Date | null, aggregatedBy: AggregationType | null) => {
		setFromDate(from);
		setToDate(to);
		setAggregatedBy(aggregatedBy ?? undefined);
	};

	const hasActiveDateFilter = Boolean(fromDate || toDate);

	const handleApplyFilter = (from: Date | null, to: Date | null) => {
		setDateFrom(from);
		setDateTo(to);
	};

	const formatDateRange = () => {
		if (!dateFrom || !dateTo) return "Select Period";
		const options: Intl.DateTimeFormatOptions = { month: "short", day: "2-digit", year: "numeric" };
		return `${dateFrom.toLocaleDateString("en-US", options)} - ${dateTo.toLocaleDateString("en-US", options)}`;
	};

	const formatDateRangeChart = () => {
		if (!fromDate || !toDate) return "Add Filter";
		const from = fromDate.toLocaleDateString("en-US", { month: "short", day: "numeric" });
		const to = toDate.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
		return `${from} - ${to}`;
	};

	const formatCurrency = (value: number): string => {
		const money = new Money("NGN", value);
		return money.toLocaleString();
	};

	useEffect(() => {
		const fetchDashboardSummary = async () => {
			try {
				const params = {
					...(dateFrom && { DateFrom: startOfDay(dateFrom) }),
					...(dateTo && { DateTo: nextDayStart(dateTo) }),
				};
				const data = await getUtilityDashboardSummary(params).unwrap();
				setDashboardData(data);
			} catch (err: any) {
				getErrorMessage(err.data);
			}
		};
		fetchDashboardSummary();
	}, [dateFrom, dateTo, getUtilityDashboardSummary]);

	useEffect(() => {
		getUserInfo()
			.unwrap()
			.catch(err => getErrorMessage(err.data));
	}, [getUserInfo]);

	useEffect(() => {
		dispatch(showLoader());
		getAggregatedTransactions({
			DateFrom: fromDate ? startOfDay(fromDate) : undefined,
			DateTo: toDate ? nextDayStart(toDate) : undefined,
			AggregationType: aggregatedBy,
			PurposeType: transactionPurposeType?.key,
		})
			.unwrap()
			.catch(err => getErrorMessage(err.data))
			.finally(() => dispatch(hideLoader()));
	}, [fromDate, toDate, aggregatedBy, transactionPurposeType, getAggregatedTransactions]);

	useEffect(() => {
		dispatch(showLoader());
		if (!aggregatedTransactions) {
			setDashboardChartProps(undefined);
			dispatch(hideLoader());
			return;
		}
		let group: UtilAdminDashboardChartGroup;

		if (fromDate && toDate && aggregatedBy !== undefined) {
			const amountByPeriod = new Map<string, number>();
			for (const item of aggregatedTransactions) {
				const key = getPeriodKey(item.period, aggregatedBy);
				amountByPeriod.set(key, item.totalAmount);
			}
			group = [];
			for (const { key, periodIso } of getPeriodsBetween(startOfDay(fromDate), endOfDay(toDate), aggregatedBy)) {
				group.push({
					consumption: amountByPeriod.get(key) ?? 0,
					generatorName: formatPeriodLabel(periodIso, aggregatedBy),
				});
			}
		} else {
			group = aggregatedTransactions.map(item => ({
				consumption: item.totalAmount,
				generatorName: formatPeriodLabel(item.period, aggregatedBy),
			}));
		}
		dispatch(hideLoader());
		setDashboardChartProps({ data: [group], transactionPurposeType: getTransactionPurposeTypeString(transactionPurposeType?.key) });
	}, [aggregatedTransactions, aggregatedBy, fromDate, toDate, transactionPurposeType?.key]);

	return (
		<>
			<DateRangeFilter isOpen={isFilterOpen} onClose={() => setIsFilterOpen(false)} onApply={handleApplyFilter} initialFrom={dateFrom} initialTo={dateTo} />

			<DateRangeFilter
				isOpen={isDistrictFilterOpen}
				onClose={() => setIsDistrictFilterOpen(false)}
				onApply={handleApplyDistrictFilter}
				initialFrom={districtDateFrom}
				initialTo={districtDateTo}
			/>

			<DateRangeFilter
				isOpen={isDateFilterOpen}
				onClose={() => setIsDateFilterOpen(false)}
				onApply={handleApplyDateFilter}
				initialFrom={fromDate}
				initialTo={toDate}
				initialAggregateBy={aggregatedBy}
				showAggregateByOptions={true}
			/>
			<div className={styles.container}>
				<div className={styles.header}>
					<h3 className={styles.userName}>Welcome {userInfo ? userInfo.firstName + " " + userInfo.lastName : ""},</h3>
					<p className={styles.userIns}>Check out the Latest Activities</p>
				</div>
				<div className={styles.bodyContainer}>
					<div className={styles.summarySection}>
						<div className={styles.summaryHeader}>
							<div>
								<h3 className={styles.sectionTitle}>Summary</h3>
							</div>
							<div className={styles.summaryHeaderActions}>
								{isFetching && <ClipLoader size={20} color="#DE841B" />}
								<Button
									variant="outline"
									onClick={() => setIsFilterOpen(true)}
									leftIcon={<FilterIcon2 style={{ marginRight: 3 }} />}
									text={`Period: ${formatDateRange()}`}
								/>
							</div>
						</div>
						<div className={styles.cardsGrid}>
							<SummaryCard
								title="Total Transactions"
								value={dashboardData ? formatCurrency(dashboardData.totalTransactionsVolume.value) : "N/A"}
								percentageChange={dashboardData?.totalTransactionsVolume.percentageChange ?? 0}
							/>
							<SummaryCard
								title="Total Transaction Count"
								value={dashboardData ? dashboardData.totalTransactionCount.value.toString() : "N/A"}
								percentageChange={dashboardData?.totalTransactionCount.percentageChange ?? 0}
							/>
							<SummaryCard
								title="Successful Transactions"
								value={dashboardData ? String(dashboardData.successfulTransactions.value) : "N/A"}
								percentageChange={dashboardData?.successfulTransactions.percentageChange ?? 0}
							/>
							<SummaryCard
								title="Failed Transactions"
								value={dashboardData ? String(dashboardData.failedTransactions.value) : "N/A"}
								percentageChange={dashboardData?.failedTransactions.percentageChange ?? 0}
							/>
							<SummaryCard
								title="Prepaid Transaction Volume"
								value={dashboardData ? formatCurrency(dashboardData.prepaidTransactionsVolume.value) : "N/A"}
								percentageChange={dashboardData?.prepaidTransactionsVolume.percentageChange ?? 0}
							/>
							<SummaryCard
								title="Postpaid Transaction Volume"
								value={dashboardData ? formatCurrency(dashboardData.postpaidTransactionsVolume.value) : "N/A"}
								percentageChange={dashboardData?.postpaidTransactionsVolume.percentageChange ?? 0}
							/>
							<SummaryCard
								title="Total Sales"
								value={dashboardData ? `${dashboardData.totalSales.value.toLocaleString()} kWh` : "N/A"}
								percentageChange={dashboardData?.totalSales.percentageChange ?? 0}
							/>
							<SummaryCard
								title="Total Sign Up"
								value={dashboardData ? dashboardData.totalSignUp.value.toString() : "N/A"}
								percentageChange={dashboardData?.totalSignUp.percentageChange ?? 0}
							/>
							<SummaryCard
								title="Users Registered Today"
								value={dashboardData ? String(dashboardData.usersRegisteredToday.value) : "N/A"}
								percentageChange={dashboardData?.usersRegisteredToday.percentageChange ?? 0}
							/>
							<SummaryCard
								title="Unique User Login"
								value={dashboardData ? String(dashboardData.uniqueUserLogins.value) : "N/A"}
								percentageChange={dashboardData?.uniqueUserLogins.percentageChange ?? 0}
							/>
							<SummaryCard
								title="Energy Profile Setup"
								value={dashboardData ? String(dashboardData.totalRegisteredUsers.value) : "N/A"}
								percentageChange={dashboardData?.totalRegisteredUsers.percentageChange ?? 0}
							/>
							<SummaryCard
								title="Transacting Customers"
								value={dashboardData ? String(dashboardData.transactingCustomers.value) : "N/A"}
								percentageChange={dashboardData?.transactingCustomers.percentageChange ?? 0}
							/>
							<SummaryCard
								title="Total RTL Request"
								value={dashboardData ? String(dashboardData.totalRTLRequest.value) : "N/A"}
							/>
							<SummaryCard
								title="Successful RTL"
								value={dashboardData ? String(dashboardData.successfulRTL.value) : "N/A"}
								sharePercentage={dashboardData?.successfulRTL.sharePercentage ?? undefined}
							/>
							<SummaryCard
								title="Failed RTL"
								value={dashboardData ? String(dashboardData.failedRTL.value) : "N/A"}
								sharePercentage={dashboardData?.failedRTL.sharePercentage ?? undefined}
							/>
							<SummaryCard
								title="Total Service Requests Submitted"
								value={dashboardData ? String(dashboardData.totalServiceRequestsSubmitted.value) : "N/A"}
								percentageChange={dashboardData?.totalServiceRequestsSubmitted.percentageChange ?? 0}
							/>
						</div>
					</div>
				</div>

				<div className={styles.bodyContainer}>
					<div className={styles.summarySection}>
						<div className={styles.summaryHeader}>
							<div className={styles.sectionTitleGroup}>
								<h3 className={styles.sectionTitle}>District Contribution Via {name}</h3>
								{districtAnalyticsInfo && (
									<InfoTooltip title={districtAnalyticsInfo.title} description={districtAnalyticsInfo.description} />
								)}
							</div>
							{districtAnalyticsEnabled && (
								<div className={styles.summaryHeaderActions}>
									{isDistrictFetching && <ClipLoader size={20} color="#DE841B" />}
									<Button
										variant="outline"
										onClick={() => setIsDistrictFilterOpen(true)}
										leftIcon={<FilterIcon2 style={{ marginRight: 3 }} />}
										text={`Period: ${formatDistrictDateRange()}`}
									/>
								</div>
							)}
						</div>
						{/* Section-level flag: the rest of the dashboard stays usable, only this block locks. */}
						{districtAnalyticsEnabled ? (
							<DistrictRevenue dateFrom={districtDateFrom} dateTo={districtDateTo} onLoadingChange={setIsDistrictFetching} />
						) : (
							!areFlagsLoading && <FeatureLockedState featureName={districtAnalyticsInfo?.title ?? "District Revenue Analytics"} />
						)}
					</div>
				</div>
				<div className={styles.bodyContainer}>
					<div className={styles.summarySection}>
						<div className={styles.summaryHeader}>
							<div>
								<h3 className={styles.sectionTitle}>Breakdown</h3>
							</div>

							<div className={styles.filterRow}>
								<CommonSelect
									items={popupFilterValues}
									selected={transactionPurposeType ?? TransactionPurposeType.Billing}
									placeholder="Select Specific Transactions"
									onChange={item => {
										setTransactionPurposeType(item);
									}}
									searchable={false}
								/>

								<Button
									variant="outline"
									onClick={() => setIsDateFilterOpen(true)}
									leftIcon={<FilterIcon2 style={{ marginRight: 3 }} />}
									text={`Period: ${formatDateRangeChart()}`}
								/>
							</div>
						</div>

						<div>
							<UtilAdminDashboardChart {...dashboardChartProps} />
						</div>
					</div>
				</div>
			</div>
		</>
	);
};
